import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { LayoutDashboard, Key, Wallet, LogOut, Menu, X, Sun, Moon, Plus, Copy, ReceiptText } from "lucide-react";
import { useState, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useTheme } from "next-themes";
import { toast, Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import Logo from "../imports/Logo-1/Logo-134-42";
import { UI_LANGUAGES } from "../i18n/languages";
import {
  useDashboardOverview,
  useUsdtBalance,
  useRefreshOverview,
} from "../stores/modules/dashboard";
import { useAuthStore } from "../stores/modules/auth";
import { clearUserScopedStores } from "../stores/utils/clearUserScoped";
import { formatUsdtBalance } from "../utils";
import { useAutoRefreshOverview } from "./hooks/useAutoRefreshOverview";
import { useAuthedBootstrap } from "./hooks/useAuthedBootstrap";
import { RechargeModal } from "./components/RechargeModal";
import { HashAvatar } from "./components/ui/hash-avatar";
import { LanguageFlagIcon } from "./components/ui/language-flag-icon";

const emptySubscribe = () => () => {};

export function Layout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const { theme, setTheme } = useTheme();
  const mounted = useSyncExternalStore(emptySubscribe, () => true, () => false);

  // 启动 dashboard overview 自动刷新（USDT 余额 / 当月积分 / 订阅）
  useAutoRefreshOverview();
  // 登录态进入 Layout 时一次性预热 alertEmail / subscription plans
  useAuthedBootstrap();
  const overview = useDashboardOverview();
  const usdtBalance = useUsdtBalance();
  const refreshOverview = useRefreshOverview();
  // 仅在"从未拿到 overview"时显示占位，已有数据后自动刷新不再回退占位，避免余额跳动
  const hasNeverLoaded = overview === null;

  // 获取用户信息
  const userInfo = useAuthStore((state) => state.userInfo);
  const walletAddress = userInfo?.walletAddress || '';
  
  // 格式化钱包地址：前2位 + ... + 后4位
  const formatAddress = (address: string) => {
    if (!address || address.length < 6) return address;
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };
  
  const displayAddress = formatAddress(walletAddress);

  useEffect(() => {
    document.title = t("layout.docTitle");
    document.documentElement.lang = i18n.resolvedLanguage || "en";
  }, [t, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    if (!isLangMenuOpen) return;
    const close = () => setIsLangMenuOpen(false);
    const onPointerDown = (e: PointerEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isLangMenuOpen]);

  const navigation = useMemo(
    () => [
      { name: t("layout.navDashboard"), href: "/", icon: LayoutDashboard },
      { name: t("layout.navKeys"), href: "/keys", icon: Key },
      { name: t("layout.navSubscription"), href: "/recharge", icon: Wallet },
      { name: t("layout.navBilling"), href: "/billing", icon: ReceiptText },
    ],
    [t]
  );

  const currentLang = useMemo(
    () => UI_LANGUAGES.find((l) => l.lng === i18n.resolvedLanguage) ?? UI_LANGUAGES[0],
    [i18n.resolvedLanguage]
  );

  // 用户菜单弹窗状态
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // 充值弹窗状态
  const [isRechargeOpen, setIsRechargeOpen] = useState(false);

  // 点击外部关闭用户菜单
  useEffect(() => {
    if (!isUserMenuOpen) return;
    const close = () => setIsUserMenuOpen(false);
    const onPointerDown = (e: PointerEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isUserMenuOpen]);

  const handleLogout = () => {
    // 清除 auth store
    const { logout } = useAuthStore.getState();
    logout();

    // 清除账号相关持久化缓存（apps / dashboard / credits / deposit），
    // 避免下次用其他账号登录时先闪过上一账号的数据。
    clearUserScopedStores();

    // 跳转到登录页
    navigate("/login");
    toast.success(t("layout.loggedOut"));
  };

  const copyAddress = () => {
    if (!walletAddress) {
      toast.error(t("layout.noWalletAddress"));
      return;
    }
    navigator.clipboard.writeText(walletAddress);
    toast.success(t("layout.addressCopied"));
  };

  return (
    <div className="flex h-screen bg-white dark:bg-[#0E0F11] text-slate-900 dark:text-slate-200 font-sans transition-colors duration-200">
      <Toaster
        position="top-center"
        theme={theme as "light" | "dark" | "system"}
        toastOptions={{
          className:
            "border border-slate-200 dark:border-slate-800 shadow-none bg-slate-50 dark:bg-[#1a1d1f] text-slate-900 dark:text-slate-200",
        }}
      />
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 cursor-pointer bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-56 bg-slate-50 dark:bg-[#1a1d1f] border-r border-slate-200 dark:border-slate-800 transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:w-56 flex flex-col ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <Logo className="w-8 h-8" />
            <span className="tracking-tight text-slate-900 dark:text-white text-[15px]">{t("layout.brand")}</span>
          </div>
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
          {navigation.map((item) => {
            // 精确匹配或子路径匹配
            const isActive =
              location.pathname === item.href ||
              (item.href !== "/" && location.pathname.startsWith(item.href + "/"));
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-600 dark:bg-[#00FFA3]/10 dark:text-[#00FFA3]"
                    : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-[#D2F0E1]/5 dark:hover:text-slate-200"
                }`}
              >
                <item.icon
                  className={`w-5 h-5 ${isActive ? "text-emerald-600 dark:text-[#00FFA3]" : "text-slate-400 dark:text-slate-400"}`}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <header className="h-16 px-4 md:px-6 border-b border-slate-200 dark:border-slate-800 bg-transparent flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4 lg:hidden">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 text-slate-500 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <Logo className="w-6 h-6" />
            </div>
          </div>

          <div className="hidden lg:block flex-1" />

          <div className="flex items-center gap-3 md:gap-5 ml-auto">
            <div className="hidden sm:flex items-center bg-slate-50 dark:bg-[#1a1d1f] rounded-full p-1 pl-4 border border-slate-200 dark:border-slate-800 transition-colors hover:border-emerald-300 dark:hover:border-[#00FFA3]/30">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                {t("layout.balanceLabel")}
                {hasNeverLoaded ? (
                  <span
                    className="inline-block w-16 h-4 rounded bg-slate-200 dark:bg-slate-700 animate-pulse"
                    aria-label={t("layout.balanceLabel")}
                  />
                ) : (
                  <span className="font-bold text-emerald-600 dark:text-[#00FFA3] tracking-tight">
                    {`${formatUsdtBalance(usdtBalance)} USDT`}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => setIsRechargeOpen(true)}
                className="bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] p-1.5 rounded-full hover:opacity-90 transition-colors ml-2.5"
                aria-label={t("dashboard.recharge")}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="relative z-40" ref={userMenuRef}>
              <div 
                className="flex items-center gap-2.5 px-3 py-1.5 bg-slate-50 dark:bg-[#1a1d1f] rounded-full cursor-pointer border border-slate-200 dark:border-slate-800 hover:border-emerald-300 dark:hover:border-[#00FFA3]/50 transition-colors"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              >
                <HashAvatar address={walletAddress} />
                <div className="flex flex-col pr-1">
                  <span className="text-xs font-medium text-slate-900 dark:text-slate-100 leading-none">{displayAddress}</span>
                </div>
              </div>

              {isUserMenuOpen && (
                <div className="absolute right-0 top-full pt-2 w-52 z-50 origin-top-right">
                  <div className="bg-slate-50 dark:bg-[#1a1d1f] rounded-xl py-2 border border-slate-200 dark:border-slate-800 shadow-lg">
                    <button
                      type="button"
                      onClick={copyAddress}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#D2F0E1]/5 flex items-center gap-2.5 transition-colors font-medium"
                    >
                      <Copy className="w-4 h-4 text-slate-400" /> {t("layout.copyAddress")}
                    </button>
                    <div className="h-px bg-slate-200 dark:bg-[#D2F0E1]/5 my-1 mx-2" />
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 flex items-center gap-2.5 transition-colors font-medium"
                    >
                      <LogOut className="w-4 h-4 text-red-500" /> {t("layout.disconnect")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="w-px h-6 bg-slate-200 dark:bg-[#D2F0E1]/5 mx-1" />

            {mounted && (
              <button
                type="button"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-slate-50 dark:bg-[#1a1d1f] border border-slate-200 dark:border-slate-800 hover:border-emerald-300 dark:hover:border-[#00FFA3]/50 text-slate-500 dark:text-slate-400 transition-colors"
                aria-label={t("layout.toggleDark")}
              >
                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            )}

            <div className="relative z-40" ref={langMenuRef}>
              <button
                type="button"
                onClick={() => setIsLangMenuOpen((open) => !open)}
                className="flex size-[34px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-50 text-lg leading-none text-slate-500 transition-colors hover:border-emerald-300 dark:border-slate-800 dark:bg-[#1a1d1f] dark:text-slate-400 dark:hover:border-[#00FFA3]/50"
                aria-label={`${t("layout.switchLanguage")}: ${currentLang.name}`}
                aria-expanded={isLangMenuOpen}
              >
                <span className="flex items-center justify-center select-none" aria-hidden>
                  <LanguageFlagIcon
                    code={currentLang.flagCode ?? currentLang.code}
                    className="h-[15px] w-[22px]"
                  />
                </span>
              </button>

              <div
                className={`absolute right-0 top-full pt-2 w-[360px] z-50 origin-top-right transition-all ${
                  isLangMenuOpen ? "block" : "hidden"
                }`}
              >
                <div className="bg-slate-50 dark:bg-[#0E0F11] rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
                  <div className="grid grid-cols-2 gap-2">
                    {UI_LANGUAGES.map((l) => {
                      const selected = i18n.resolvedLanguage === l.lng;
                      return (
                        <button
                          key={l.lng}
                          type="button"
                          aria-label={l.name}
                          onClick={() => {
                            void i18n.changeLanguage(l.lng);
                            setIsLangMenuOpen(false);
                          }}
                          className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors text-left w-full ${
                            selected
                              ? "bg-emerald-50 dark:bg-[#00FFA3]/10 ring-2 ring-emerald-500 dark:ring-[#00FFA3]/50"
                              : "hover:bg-slate-100 dark:hover:bg-[#141517]"
                          }`}
                        >
                          <div
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${
                              selected
                                ? "border-emerald-500 bg-white dark:border-[#00FFA3] dark:bg-[#1a1d1f]"
                                : "border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-[#1a1d1f]"
                            }`}
                          >
                            <span className="flex items-center justify-center select-none" aria-hidden>
                              <LanguageFlagIcon
                                code={l.flagCode ?? l.code}
                                className="h-[16px] w-[24px]"
                              />
                            </span>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{l.name}</span>
                            <span className="text-[11px] text-slate-400 dark:text-slate-500 truncate">{l.sub}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </div>
      </main>

      <RechargeModal
        open={isRechargeOpen}
        onClose={() => setIsRechargeOpen(false)}
        onSuccess={() => {
          void refreshOverview();
        }}
      />
    </div>
  );
}
